import i18next from 'i18next';

/**
 * Date parsing rules:
 *  - Date-only (YYYY-MM-DD): parse as local midnight — no offset, avoids UTC-shift off-by-one
 *  - DateTime without offset: backend stores HKT (GMT+8), so append +08:00
 *  - Already has Z or offset: keep as-is
 */
/** Format a Date to YYYY-MM-DD using local timezone (avoids UTC offset off-by-one). */
export function formatLocalDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function parseServerDate(d: string): Date {
  if (!d) return new Date(NaN);
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return new Date(d + 'T00:00:00');
  if (d.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(d)) return new Date(d);
  return new Date(d + '+08:00');
}

/** 取多個日期字串中最新的一個（無效值忽略）。 */
export function latestDate(...dates: (string | undefined | null)[]): string | undefined {
  let latest: string | undefined;
  let latestMs = -Infinity;
  for (const d of dates) {
    if (!d) continue;
    const ms = parseServerDate(d).getTime();
    if (!isNaN(ms) && ms > latestMs) {
      latestMs = ms;
      latest = d;
    }
  }
  return latest;
}

/** 相對時間顯示：X分鐘前/X小時前/X天前，超過30天顯示日期。顯示為瀏覽器本地時間。 */
export function formatRelativeTime(d?: string | null): string {
  if (!d) return '';
  const ms = parseServerDate(d).getTime();
  if (isNaN(ms)) return '';
  const mins = Math.floor((Date.now() - ms) / 60000);
  const hrs = Math.floor(mins / 60);
  const days = Math.floor(hrs / 24);
  if (mins < 1) return i18next.t('common.justNow', 'Just now');
  if (mins < 60) return i18next.t('common.minutesAgo', { n: mins, defaultValue: `${mins} min ago` });
  if (hrs < 24) return i18next.t('common.hoursAgo', { n: hrs, defaultValue: `${hrs} hr ago` });
  if (days < 30) return i18next.t('common.daysAgo', { n: days, defaultValue: `${days} days ago` });
  return formatDate(d);
}

/** Format YYYY-MM or YYYY-MM-DD to YYYY/MM (for work experience periods). */
export function formatYearMonth(d?: string | null): string {
  if (!d) return '';
  try {
    const date = parseServerDate(d);
    if (isNaN(date.getTime())) return d;
    return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}`;
  } catch { return d; }
}

/** Calculate duration in years+months between two date strings (endDate defaults to now). */
export function calculateDuration(startDate: string, endDate?: string): { years: number; months: number } {
  const start = parseServerDate(startDate);
  const end = endDate ? parseServerDate(endDate) : new Date();
  const totalMonths = Math.max(0, (end.getFullYear() - start.getFullYear()) * 12 + end.getMonth() - start.getMonth());
  return { years: Math.floor(totalMonths / 12), months: totalMonths % 12 };
}

/**
 * Format datetime to YYYY/MM/DD HH:mm in the viewer's local timezone.
 * Used for last-active, last-edit times — backend stores HKT, frontend shows in viewer's tz.
 */
export function formatDateTime(d?: string | null): string {
  if (!d) return '—';
  const date = parseServerDate(d);
  if (isNaN(date.getTime())) return d;
  const [datePart, timePart] = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(date).split(', ');
  return `${datePart.replace(/-/g, '/')} ${timePart}`;
}

/** Calculate age from date-of-birth string. Returns null for invalid input. */
export function calculateAge(dob?: string | null): number | null {
  if (!dob) return null;
  const birth = parseServerDate(dob);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

/** Format any date string or Date to YYYY/MM/DD. Returns '' for falsy input. */
export function formatDate(d?: string | Date | null): string {
  if (!d) return '';
  try {
    const date = typeof d === 'string' ? parseServerDate(d) : d;
    if (isNaN(date.getTime())) return typeof d === 'string' ? d : '';
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(date).replace(/-/g, '/');
  } catch {
    return typeof d === 'string' ? d : '';
  }
}
